<script setup lang="ts">
import { computed } from "vue";
import { aggValue, currency } from "../plugins/Formatting.js";
import { RfpLocation } from "../types/Marketplace";

const props = defineProps<{
  location: RfpLocation;
}>();

// Build location data
const buildLocationData = () => {
  return {
    name: "Location",
    fields: [
      {
        name: "Address",
        description: "Formatted street address",
        value: `${props.location.address.formatted}`,
      },
      // Add available parcel area if it is specified.
      ...(props.location.data.availableParcelAreaSqft
        ? [
            {
              name: "Total usable parcel area",
              description:
                "Total area of the land parcel(s) that is usable for a project",
              value: aggValue(
                props.location.data.availableParcelAreaSqft,
                "sqft",
              ),
            },
          ]
        : []),
      ...(props.location.data.roofAreaSqft
        ? [
            {
              name: "Total rooftop area",
              description:
                "Total area of the rooftop(s) that is usable for a project",
              value: aggValue(props.location.data.roofAreaSqft, "sqft"),
            },
          ]
        : []),
    ],
  };
};

const buildGridData = () => {
  return {
    name: "Grid",
    fields: [
      {
        name: "Utility",
        description: "Utility service provider",
        value: props.location.data.utilityName,
      },
      ...(props.location.data.averageMonthlyBillUsd
        ? [
            {
              name: "Average monthly bill",
              description: "Average monthly bill based on actual data",
              // Compensate for the fact that $format.formatting.currency expects values in cents.
              value: currency(
                props.location.data.averageMonthlyBillUsd * 100,
                0,
              ),
            },
          ]
        : []),
    ],
  };
};

const buildEnergyData = () => {
  const result = {
    name: "Energy",
    fields: [
      ...(props.location.data.annualUsageKwh
        ? [
            {
              name: "Total annual usage",
              description: "Total annual usage based on actual data",
              value: aggValue(props.location.data.annualUsageKwh, "kWh"),
            },
          ]
        : []),
      ...(props.location.data.annualPeakKw
        ? [
            {
              name: "Maximum annual peak",
              description: "Maximum annual peak based on actual data",
              value: aggValue(props.location.data.annualPeakKw, "kW"),
            },
          ]
        : []),
    ],
  };
  return result.fields.length > 0 ? result : null;
};

const projectData = computed(() => {
  const locationData = buildLocationData();
  const gridData = buildGridData();
  const energyData = buildEnergyData();

  const result = [];
  for (const data of [locationData, gridData, energyData]) {
    if (data) {
      result.push(data);
    }
  }
  return result;
});
</script>

<template>
  <t-card v-if="projectData">
    <t-card-text>
      <div v-for="(group, i) of projectData" :key="`group-${i}`">
        <hr
          class="tw:border-zinc-500/50 tw:my-4"
          v-if="i > 0"
          :key="`divider-${i}`"
        />
        <h3
          class="tw:sta-subheading tw:pb-2 tw:text-success-400! tw:font-semibold tw:px-0"
        >
          <span v-html="group.name" />
        </h3>
        <div v-for="(field, j) of group.fields" :key="`field-${i}-${j}`">
          <div class="tw:md:flex tw:justify-between tw:m-4">
            <div class="tw:flex tw:flex-col tw:md:w-1/2">
              <span v-html="field.name" class="tw:text-base" />
              <span class="tw:text-zinc-400" v-html="field.description" />
            </div>
            <span
              class="tw:text-base tw:font-semibold tw:md:text-right"
              v-html="field.value"
            />
          </div>
        </div>
      </div>
    </t-card-text>
  </t-card>
</template>
